import { useAuth } from "@assetwatch/auth";
import {
  Alert,
  Button,
  Container,
  FullPageLoading,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import { useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnterpriseRouteFrame } from "./EnterpriseRouteFrame";
import { useLaunchDarklyStatus } from "./LaunchDarkly/LaunchDarklyProvider";
import { useFeatureFlags } from "./LaunchDarkly/useFeatureFlags";

export function EnterpriseFeatureGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const hasLaunchDarklyClient = Boolean(
    import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID,
  );
  const { isReady } = useLaunchDarklyStatus();
  const { enterpriseAppEnabled, hasEnterpriseAppEnabledFlag } =
    useFeatureFlags();
  const [hasGrantedEnterpriseAccess, setHasGrantedEnterpriseAccess] =
    useState(false);

  useEffect(() => {
    if (isReady && hasEnterpriseAppEnabledFlag && enterpriseAppEnabled) {
      setHasGrantedEnterpriseAccess(true);
    }
  }, [enterpriseAppEnabled, hasEnterpriseAppEnabledFlag, isReady]);

  if (!hasLaunchDarklyClient || pathname === "/login/reset") {
    return <>{children}</>;
  }

  if (!isReady || !hasEnterpriseAppEnabledFlag) {
    if (hasGrantedEnterpriseAccess) {
      return <>{children}</>;
    }

    return <FullPageLoading />;
  }

  if (enterpriseAppEnabled) {
    return <>{children}</>;
  }

  return (
    <EnterpriseRouteFrame>
      <Container size="sm">
        <Stack gap={16}>
          <Title size="lg">
            {t("enterprise.chrome.featureGate.disabledTitle")}
          </Title>
          <Alert
            tone="informative"
            title={t("enterprise.chrome.featureGate.disabledDescription")}
          />
          <Text tone="subtle">
            {t("enterprise.chrome.featureGate.rolloutDescription")}
          </Text>
          <Button
            data-testid="enterprise-feature-gate-sign-out"
            icon="logout-exit"
            type="button"
            variant="outline"
            onClick={() => void signOut()}
          >
            {t("enterprise.common.signOut")}
          </Button>
        </Stack>
      </Container>
    </EnterpriseRouteFrame>
  );
}
